import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Lock, ArrowRight } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useAppState } from "@/context/StateContext";
import { api } from "@/lib/api";
import { fmt } from "@/lib/format";

export function PaymentReviewModal({ open, onOpenChange, sprint, onCompleted }) {
  const { refresh } = useAppState();
  const [amounts, setAmounts] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (sprint) {
      const init = {};
      sprint.allocations.forEach((a) => {
        init[a.debt_id] = String(a.recommended);
      });
      setAmounts(init);
    }
  }, [sprint]);

  if (!sprint) return null;

  const setAmount = (id, val) =>
    setAmounts((prev) => ({ ...prev, [id]: val.replace(/[^0-9.]/g, "") }));

  const total = sprint.allocations.reduce(
    (sum, a) => sum + (parseFloat(amounts[a.debt_id] || "0") || 0),
    0
  );

  const markPaid = async () => {
    const payments = sprint.allocations.map((a) => ({
      debt_id: a.debt_id,
      actual_amount: parseFloat(amounts[a.debt_id] || "0") || 0,
    }));
    setLoading(true);
    try {
      const completed = await api.confirm(sprint.id, payments);
      onOpenChange(false);
      onCompleted(completed);
      await refresh();
    } catch (e) {
      toast.error("Could not confirm payments. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-lg gap-0 rounded-2xl border-[#E5E0D9] bg-[#FAF8F4] p-0"
        data-testid="payment-review-modal"
      >
        <div className="p-6 sm:p-7">
          <DialogHeader className="space-y-2 text-left">
            <DialogTitle className="font-serif text-2xl font-normal text-[#171717]">
              Confirm your payments
            </DialogTitle>
            <DialogDescription className="text-[15px] text-[#525252]">
              You pay each company yourself. Then tell us what you actually paid. It's fine if it's different from what we suggested.
            </DialogDescription>
          </DialogHeader>

          {/* Non-color reliant disclaimer with icon + text */}
          <div
            className="mt-5 flex items-start gap-2.5 rounded-xl border border-[#E5E0D9] bg-[#F5F2EC] p-3.5 text-[13px] leading-relaxed text-[#70584A]"
            data-testid="execute-payments-disclaimer"
          >
            <Lock className="mt-0.5 h-4 w-4 shrink-0 text-[#2F6B57]" />
            <span>
              <strong className="text-[#164C3B]">The Paycheck Edit doesn't move your money.</strong> Pay each company in your own bank or app, then confirm here. A suggestion isn't a payment until you make it.
            </span>
          </div>

          <div className="mt-5 space-y-3">
            {sprint.allocations.map((a) => (
              <div
                key={a.debt_id}
                className="rounded-xl pe-surface p-4"
                data-testid={`payment-row-${a.debt_id}`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-medium text-[#171717]">{a.name}</span>
                  <span className="text-xs text-[#70584A]">
                    We suggested {fmt(a.recommended)}
                  </span>
                </div>
                <div className="mt-2.5 flex items-center rounded-lg border border-[#E5E0D9] bg-[#FAF8F4] px-3 focus-within:ring-2 focus-within:ring-[#2F6B57]">
                  <span className="text-lg text-[#164C3B]">$</span>
                  <input
                    inputMode="decimal"
                    type="text"
                    data-testid={`payment-input-${a.debt_id}`}
                    value={amounts[a.debt_id] ?? ""}
                    onChange={(e) => setAmount(a.debt_id, e.target.value)}
                    className="w-full bg-transparent py-2.5 pl-1.5 text-lg text-[#171717] outline-none"
                  />
                </div>
              </div>
            ))}
          </div>

          <div className="mt-4 flex items-center justify-between rounded-xl bg-[#DDE7DF] px-4 py-3">
            <span className="text-sm font-medium text-[#164C3B]">Total you paid</span>
            <span className="font-serif text-xl text-[#164C3B]" data-testid="payment-review-total">
              {fmt(total)}
            </span>
          </div>

          <div className="mt-6 flex flex-col gap-2.5 sm:flex-row-reverse">
            <Button
              data-testid="mark-payments-complete-button"
              onClick={markPaid}
              disabled={loading}
              className="min-h-12 flex-1 rounded-xl bg-[#2F6B57] text-base font-semibold text-white hover:bg-[#255643]"
            >
              {loading ? "Confirming…" : "I made the payment"}
              {!loading && <ArrowRight className="ml-1 h-4 w-4" />}
            </Button>
            <Button
              variant="outline"
              data-testid="payment-not-yet-button"
              onClick={() => onOpenChange(false)}
              className="min-h-12 flex-1 rounded-xl border-[#E5E0D9] bg-white text-base font-medium text-[#70584A] hover:bg-[#F5F2EC]"
            >
              Not yet
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
